// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Search from "@store/Library/Components/Toolkit/Icons/Search";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import LoadingOverlay, { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { buttonGetFieldIconProps } from "@store/Themes/common/props/button";
import { tableProps } from "@store/Themes/common/props/table";
import findWarrantyBySerial from "@store/Framework/Store/Store/Pages/ProductWarrantyLookup/Handlers/WarrantyLookupBySerial";
import findWarrantyBySerialAndModel from "@store/Framework/Store/Store/Pages/ProductWarrantyLookup/Handlers/WarrantyLookupBySerialAndModel";
import { useProductWarrantyLookup } from "@store/Framework/Store/Store/Pages/ProductWarrantyLookup/StoreProductWarrantyLookupHooks";
import TextField, { TextFieldPresentationProps } from "@store/MobiusOverrides/TextField/TextField";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapDispatchToProps = {
    findWarrantyBySerial,
    findWarrantyBySerialAndModel,
};

type Props = ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductWarrantyLookupStyles {
    smallGridItem?: GridItemProps;
    largeGridItem?: GridItemProps;
    warrantyLookupTextField?: TextFieldPresentationProps;
    sectionWrapper?: InjectableCss;
    errorInfo?: InfosBoxProps;
    loadingOverlayStyles?: LoadingOverlayProps;
    dataTableWrapper?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    table?: DataTableProps;
    tableRow?: DataTableRowProps;
    tableHeader?: DataTableHeaderProps;
    tableCell?: DataTableCellProps;
}

export const productWarrantyLookupStyles: ProductWarrantyLookupStyles = {
    smallGridItem: { width: [12, 12, 6, 6, 6] },
    largeGridItem: { width: 12 },
    warrantyLookupTextField: {
        ...buttonGetFieldIconProps(Search),
        iconClickableProps: {
            ...buttonGetFieldIconProps(Search).iconClickableProps,
            css: css`
                left: 0;
            `,
        },
        cssOverrides: {
            inputSelect: css`
                padding-right: ${pxToRem(16)};
                padding-left: ${pxToRem(43)};
            `,
        },
    },
    sectionWrapper: {
        css: css`
            width: 100%;
            padding: ${pxToRem(12)};
            background-color: ${getColor("tertiary.light")};

            ${breakpoint("lg")} {
                padding: ${pxToRem(30)};
            }
        `,
    },
    loadingOverlayStyles: {
        mergeCss: true,
        css: css`
            height: 100%;
        `,
    },
    errorInfo: {
        type: "error",
        extendedStyles: {
            wrapper: {
                css: css`
                    height: 100%;
                `,
            },
        },
    },
    dataTableWrapper: mergeProps(tableProps.wrapper, {
        css: css`
            margin-top: ${pxToRem(22)};
        `,
    }),
    sectionTitle: {
        forwardAs: "h2",
        mergeCss: true,
        css: css`
            ${({ theme }) => css`
                font-size: ${pxToRem(30)};
                font-weight: ${theme.design.typography.weight.bold};
            `}
            padding-bottom: ${pxToRem(4)};
            border-bottom: 1px solid ${getColor("tertiary.dark")};
        `,
    },
    table: {
        css: css`
            border: none;
        `,
    },
    tableRow: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            background: ${getColor("tertiary.light")};

            &:not(:last-child) {
                border-bottom: 1px solid ${getColor("common.border")};
            }

            &:nth-child(even) {
                background: ${getColor("tertiary.light")};
            }
        `,
    },
    tableHeader: {
        css: css`
            color: inherit;
            background-color: ${getColor("tertiary.light")};

            &:last-child {
                padding-bottom: none;
            }
        `,
    },
    tableCell: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};

            &:last-child {
                padding-bottom: none;
            }
        `,
    },
};

const styles = productWarrantyLookupStyles;

const ProductWarrantyLookup: FC<Props> = ({ findWarrantyBySerial, findWarrantyBySerialAndModel }) => {
    const productWarranty = useProductWarrantyLookup();
    const [serialNumber, setSerialNumber] = React.useState("");
    const trackForm = ga.use.trackEvent({ action: "gaEvent" });

    const trackEvent = (currentSearchQuery: string) => {
        trackForm({
            eventCategory: "Search",
            eventAction: "WarrantyLookup",
            eventLabel: currentSearchQuery,
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            if (serialNumber !== productWarranty.searchQuery) {
                findWarrantyBySerial({ serialNumber });
                trackEvent(serialNumber);
            }
        }
    };

    const handleOnBlur = () => {
        if (serialNumber !== productWarranty.searchQuery) {
            findWarrantyBySerial({ serialNumber });
            trackEvent(serialNumber);
        }
    };

    const changeModelNumber = (event: React.ChangeEvent<HTMLSelectElement>) => {
        findWarrantyBySerialAndModel({ serialNumber, modelNumber: event.currentTarget.value });
        trackEvent(event.currentTarget.value);
        event.currentTarget.blur();
    };

    if (!productWarranty) {
        return null;
    }

    return (
        <GridContainer>
            <GridItem {...styles.smallGridItem}>
                <TextField
                    label={translate("Search by serial number")}
                    onChange={event => setSerialNumber(event.currentTarget.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleOnBlur}
                    placeholder={translate("Enter a product serial #")}
                    iconClickableProps={{
                        ...styles.warrantyLookupTextField?.iconClickableProps,
                        onClick: () => findWarrantyBySerial({ serialNumber }),
                    }}
                    value={serialNumber}
                    type="text"
                    {...styles.warrantyLookupTextField}
                />
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <LoadingOverlay loading={productWarranty.isLoadingModels} {...styles.loadingOverlayStyles}>
                    {productWarranty.modelNumbers && (
                        <Select onChange={changeModelNumber} label={translate("Show warranty for")}>
                            <option value="">{translate("Select a model")}</option>
                            {productWarranty.modelNumbers.map(modelNumber => (
                                <option key={modelNumber} value={modelNumber}>
                                    {modelNumber}
                                </option>
                            ))}
                        </Select>
                    )}
                    {productWarranty.type === 2 && (
                        <InfosBox {...styles.errorInfo}>
                            {translate("We could not find the warranty information related to this serial number.")}
                        </InfosBox>
                    )}
                </LoadingOverlay>
            </GridItem>
            <LoadingOverlay loading={productWarranty.isLoadingData} {...styles.loadingOverlayStyles}>
                {productWarranty.data?.sections &&
                    productWarranty.data?.sections.map(section => (
                        <GridItem key={section.key} {...styles.largeGridItem}>
                            <StyledWrapper {...styles.sectionWrapper}>
                                <Typography {...styles.sectionTitle}>{section.value}</Typography>
                                <StyledWrapper {...styles.dataTableWrapper}>
                                    <DataTable {...styles.table}>
                                        <DataTableBody data-test-selector="entitlementPolicyStatementTable_tableBody">
                                            {section.details.map((row, index) => (
                                                <DataTableRow key={index} {...styles.tableRow}>
                                                    {row.names?.map((name, index) => (
                                                        <DataTableHeader {...styles.tableHeader} key={index}>
                                                            {name}
                                                        </DataTableHeader>
                                                    ))}
                                                    {row.values?.map((value, index) => (
                                                        <DataTableCell {...styles.tableCell} key={index}>
                                                            {value ?? "-"}
                                                        </DataTableCell>
                                                    ))}
                                                </DataTableRow>
                                            ))}
                                        </DataTableBody>
                                    </DataTable>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                    ))}
            </LoadingOverlay>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(ProductWarrantyLookup),
    definition: {
        group: "Common",
        displayName: "Product Warranty Lookup",
    },
};

export default widgetModule;
